import React, { useState, useEffect } from 'react';
import { Button, Skeleton } from '../../components/ui';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  People,
  PersonAdd,
  LocalOffer,
  AttachMoney,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule
} from '../../mui-icons';
import './DashboardPage.css';

const DashboardPage = () => {
  const { admin, getRoleName } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [commissionsDashboard, setCommissionsDashboard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Carregar dados em paralelo
      const [usersStats, commissionsData, influencersData, couponsData] = await Promise.all([
        adminAPI.getUsersStats().catch(() => null),
        adminAPI.getCommissionsDashboard().catch(() => null),
        adminAPI.getInfluencers({ limit: 5 }).catch(() => null),
        adminAPI.getCoupons({ limit: 5 }).catch(() => null)
      ]);

      setStats({
        users: usersStats?.data || {},
        influencers: influencersData?.data || {},
        coupons: couponsData?.data || {}
      });

      setCommissionsDashboard(commissionsData?.data || null);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Skeleton Loading
  if (loading) {
    return (
      <div className="dashboard-page">
        <Skeleton width={300} height={32} borderRadius={8} className="dashboard-welcome-skeleton" />

        <div className="dashboard-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stat-card pn-card pn-card--stat">
              <Skeleton width={48} height={48} borderRadius={12} />
              <div className="stat-content">
                <Skeleton width={80} height={16} borderRadius={4} style={{ marginBottom: 8 }} />
                <Skeleton width={60} height={32} borderRadius={4} />
              </div>
            </div>
          ))}
        </div>

        <div className="dashboard-row">
          <div className="admin-card pn-card pn-card--default" style={{ flex: 1 }}>
            <Skeleton width={150} height={24} borderRadius={4} style={{ marginBottom: 20 }} />
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} width="100%" height={50} borderRadius={8} style={{ marginBottom: 12 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error-state">
          <Warning sx={{ fontSize: 60 }} />
          <h2>Erro ao carregar dashboard</h2>
          <p>{error}</p>
          <Button variant="primary" onClick={loadDashboardData}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  const overview = commissionsDashboard?.overview || {};
  const topInfluencers = commissionsDashboard?.top_influencers || [];

  return (
    <div className="dashboard-page">
      {/* Welcome Message */}
      <div className="dashboard-welcome">
        <h2>Bem-vindo, {admin?.name || 'Administrador'}!</h2>
        <p>Painel de controle do PersonalNutri</p>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon users">
            <People />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total de Usuários</span>
            <span className="stat-value">{stats?.users?.total_users || 0}</span>
          </div>
        </div>

        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon influencers">
            <PersonAdd />
          </div>
          <div className="stat-content">
            <span className="stat-label">Influenciadores</span>
            <span className="stat-value">{overview?.total_influencers || 0}</span>
          </div>
        </div>

        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon coupons">
            <LocalOffer />
          </div>
          <div className="stat-content">
            <span className="stat-label">Cupons Ativos</span>
            <span className="stat-value">{stats?.coupons?.pagination?.total || 0}</span>
          </div>
        </div>

        <div className="stat-card pn-card pn-card--stat">
          <div className="stat-icon money">
            <AttachMoney />
          </div>
          <div className="stat-content">
            <span className="stat-label">Comissões Pendentes</span>
            <span className="stat-value">
              R$ {(overview?.pending_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Commission Overview */}
      <div className="dashboard-row">
        <div className="admin-card pn-card pn-card--default commission-overview">
          <div className="admin-card pn-card pn-card--default-header">
            <h3 className="admin-card pn-card pn-card--default-title">Resumo de Comissões</h3>
            <Link to="/admin/commissions" className="view-all-link">
              Ver todas →
            </Link>
          </div>

          <div className="commission-stats">
            <div className="commission-stat">
              <div className="commission-stat-icon pending">
                <Schedule />
              </div>
              <div className="commission-stat-content">
                <span className="commission-stat-label">Pendentes</span>
                <span className="commission-stat-value">{overview?.pending_count || 0}</span>
                <span className="commission-stat-amount">
                  R$ {(overview?.pending_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="commission-stat">
              <div className="commission-stat-icon paid">
                <CheckCircle />
              </div>
              <div className="commission-stat-content">
                <span className="commission-stat-label">Pagas</span>
                <span className="commission-stat-value">{overview?.paid_count || 0}</span>
                <span className="commission-stat-amount">
                  R$ {(overview?.paid_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="commission-stat">
              <div className="commission-stat-icon total">
                <TrendingUp />
              </div>
              <div className="commission-stat-content">
                <span className="commission-stat-label">Total</span>
                <span className="commission-stat-value">{overview?.total_payments || 0}</span>
                <span className="commission-stat-amount">
                  R$ {(overview?.total_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Influencers & Quick Links */}
      <div className="dashboard-row">
        {/* Top Influencers */}
        <div className="admin-card pn-card pn-card--default top-influencers">
          <div className="admin-card pn-card pn-card--default-header">
            <h3 className="admin-card pn-card pn-card--default-title">Top Influenciadores</h3>
            <Link to="/admin/influencers" className="view-all-link">
              Ver todos →
            </Link>
          </div>

          {topInfluencers.length > 0 ? (
            <div className="influencer-list">
              {topInfluencers.slice(0, 5).map((influencer, index) => (
                <div key={influencer.idinfluencer} className="influencer-item">
                  <div className="influencer-rank">#{index + 1}</div>
                  <div className="influencer-info">
                    <span className="influencer-name">{influencer.name}</span>
                    <span className="influencer-users">{influencer.total_users} usuários</span>
                  </div>
                  <div className="influencer-commission">
                    R$ {(influencer.total_commission || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-list">
              <p>Nenhum influenciador encontrado</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="admin-card pn-card pn-card--default quick-actions">
          <h3 className="admin-card pn-card pn-card--default-title">Ações Rápidas</h3>

          <div className="quick-links">
            <Link to="/admin/influencers" className="quick-link">
              <PersonAdd />
              <span>Novo Influenciador</span>
            </Link>
            <Link to="/admin/coupons" className="quick-link">
              <LocalOffer />
              <span>Novo Cupom</span>
            </Link>
            <Link to="/admin/users" className="quick-link">
              <People />
              <span>Ver Usuários</span>
            </Link>
          </div>

          <div className="admin-info-box">
            <h4>Seu Perfil</h4>
            <p><strong>Nome:</strong> {admin?.name || 'N/A'}</p>
            <p><strong>Role:</strong> {getRoleName()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;

